import ReactDOM from 'react-dom/client';
import React from 'react';
import { RmxServiceComponent } from './RmxServiceComponent/RmxServiceComponent';
import { getPortalServiceApi } from './api/portal-api';
import { ContextProvider, createContextWithValue } from '../utils';
import { RmxServiceMenuProvider } from './RmxServiceMenuContext';
import { openPortalServiceMenu } from './portal-context';
import { type IRmxServiceApi, RmxServiceApiProvider } from './api';
import { QueryClientProvider } from '@tanstack/react-query';
import { createStandardQueryClient } from '../utils/react-query/client';
import { BootStrapContextProvider } from '../../hooks/bootstrap-context';
import { useListenToPortalTabsClick } from '../portal-tabs-hook';
import { OnboardingContextProvider } from '../onboardings/OnboardingContext';
import { GlobalConfirmDialogProvider } from '../utils/GlobalConfirmDialog';
import { RmxServiceComponentModal, type ServiceFilterParams } from './RmxServiceComponent/RmxServiceComponentModal';
import { BypassTable } from '../utils/BypassTable/BypassTable';
import { AlertTable } from '../alert-table/AlertTable';
import { InsightsTab } from '../insights-tab/InsightsTab';
import { ComponentLifeDataProvider } from '../component-life/ComponentLifeDataContext';
import { RmxServiceContextProvider } from './RmxServiceComponent/RmxServiceContext';

const queryClient = createStandardQueryClient();

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    BootstrapRmxService: (targetId: string) => void;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    BootstrapAlertTable: (targetId: string) => void;
    openServiceModal: (f: ServiceFilterParams) => void;
    serviceApi: IRmxServiceApi;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    BootstrapBypassTable: (targetId: string) => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    BootstrapInsightsTab: (targetId: string) => void;
  }
}
const portalApi = getPortalServiceApi();
// exposing the api to portal so the last load report can be ran
window.serviceApi = portalApi;

export function RmxServiceContainer() {
  const tabBtn = document.querySelector('#tab_rmx_service_btn');
  const isVisible = tabBtn?.classList.contains('active');
  useListenToPortalTabsClick();
  const contexts = [createContextWithValue(RmxServiceApiProvider, portalApi), createContextWithValue(RmxServiceMenuProvider, openPortalServiceMenu)];
  return (
    <React.StrictMode>
      <BootStrapContextProvider version={4}>
        <QueryClientProvider client={queryClient}>
          <ContextProvider contexts={contexts}>
            <GlobalConfirmDialogProvider>
              <OnboardingContextProvider>
                {isVisible && <RmxServiceComponent />}
                <RmxServiceComponentModal />
              </OnboardingContextProvider>
            </GlobalConfirmDialogProvider>
          </ContextProvider>
        </QueryClientProvider>
      </BootStrapContextProvider>
    </React.StrictMode>
  );
}

window.BootstrapRmxService = (targetId: string) => {
  const root = ReactDOM.createRoot(document.getElementById(targetId) as HTMLElement);
  root.render(<RmxServiceContainer />);
};

window.BootstrapAlertTable = (targetId: string) => {
  const root = ReactDOM.createRoot(document.getElementById(targetId) as HTMLElement);
  root.render(
    <QueryClientProvider client={queryClient}>
      <AlertTable />
    </QueryClientProvider>
  );
};

window.BootstrapBypassTable = (targetId: string) => {
  const root = ReactDOM.createRoot(document.getElementById(targetId) as HTMLElement);
  root.render(
    <QueryClientProvider client={queryClient}>
      <BypassTable />
    </QueryClientProvider>
  );
};

window.BootstrapInsightsTab = (targetId: string) => {
  const root = ReactDOM.createRoot(document.getElementById(targetId) as HTMLElement);
  const contexts = [createContextWithValue(RmxServiceApiProvider, portalApi)];
  root.render(
    <QueryClientProvider client={queryClient}>
      <ContextProvider contexts={contexts}>
        <OnboardingContextProvider>
          {/*None of these actions are used when the activity table is not in my work*/}
          <RmxServiceContextProvider refetchServiceTable={async () => undefined} refetchMyWorkTable={async () => undefined} userRoles={[]}>
            <GlobalConfirmDialogProvider>
              <ComponentLifeDataProvider>
                <InsightsTab />
              </ComponentLifeDataProvider>
            </GlobalConfirmDialogProvider>
          </RmxServiceContextProvider>
        </OnboardingContextProvider>
      </ContextProvider>
    </QueryClientProvider>
  );
};
